/* eslint-disable */
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { X, Edit2, CheckCircle, Circle, FileText, ClipboardList, AlertTriangle, ChevronDown, ChevronUp, Clock, Award, Shield, TrendingUp, Calendar } from 'lucide-react';
import { C, inp, lbl, fmt, fmtDT, midDate, isNew, daysUntil, depositAmount, BAR_SKILLS, KIT_SKILLS, RoleIcon, BalancePill, MemberDocButton } from './teamUtils';
import ContractModal from './ContractModal';
import OnboardingChecklistModal from './OnboardingChecklistModal';
import SendOnboardingModal from './SendOnboardingModal';
import { EvaluationModal, EvaluationHistoryModal } from './Evaluation';
import WarningConductLog from './WarningConductLog';
import { LeaveOTMemberModal } from './LeaveOT';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ── Stat tile used in the dashboard grid ──────────────────────────────
function Tile({ icon, label, value, sub, status, onClick, actionLabel }) {
  // status: 'good' | 'warn' | 'alert' | 'neutral' | 'info'
  const colors = {
    good:    { bg: C.greenLight,    border: C.green+'44',  text: C.green,   icon: C.green   },
    warn:    { bg: C.amberLight,    border: C.amber+'44',  text: C.amber,   icon: C.amber   },
    alert:   { bg: C.redLight,      border: C.red+'44',    text: C.red,     icon: C.red     },
    neutral: { bg: C.cream,         border: C.border,      text: C.mid,     icon: C.mid     },
    info:    { bg: C.skyBlueLight,  border: C.skyBlue+'88',text: C.skyText, icon: C.skyText },
  };
  const s = colors[status] || colors.neutral;
  return (
    <div
      onClick={onClick}
      style={{
        background: s.bg, border: `1px solid ${s.border}`, borderRadius: '12px',
        padding: '14px 16px', cursor: onClick ? 'pointer' : 'default',
        display: 'flex', flexDirection: 'column', gap: '6px',
        transition: 'box-shadow 0.15s',
        position: 'relative', overflow: 'hidden',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, color: s.icon, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
        <span style={{ opacity: 0.5 }}>{icon}</span>
      </div>
      <div style={{ fontSize: '18px', fontWeight: 800, color: s.text, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: C.mid, lineHeight: 1.3 }}>{sub}</div>}
      {onClick && actionLabel && (
        <div style={{ fontSize: '11px', fontWeight: 600, color: s.icon, marginTop: '2px' }}>{actionLabel} →</div>
      )}
    </div>
  );
}

// ── Eitje & Balance Settings (admin only) ─────────────────────────────
function EitjeBalanceSettings({ member, currentUser, onSaved }) {
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';
  if (!isAdmin) return null;

  const [open,       setOpen]       = useState(false);
  const [fields,     setFields]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [saved,      setSaved]      = useState(false);
  const [err,        setErr]        = useState('');
  const [finding,    setFinding]    = useState(false);
  const [findResult, setFindResult] = useState(null);

  const finp = { width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'13px', color:C.charcoal, background:C.white, outline:'none', boxSizing:'border-box' };
  const flbl = { fontSize:'12px', fontWeight:600, color:C.charcoal, display:'block', marginBottom:'4px' };

  useEffect(() => {
    if (!open || fields !== null) return;
    supabase.from('team_members')
      .select('eitje_user_id, leave_accrual_pct, balance_calc_start_date, opening_ot_balance, opening_leave_balance')
      .eq('id', member.id).single()
      .then(({ data, error }) => {
        if (!error && data) setFields({
          eitje_user_id:           data.eitje_user_id           ?? '',
          leave_accrual_pct:       data.leave_accrual_pct       ?? 8.0,
          balance_calc_start_date: data.balance_calc_start_date || member.join_date || '',
          opening_ot_balance:      data.opening_ot_balance      ?? 0,
          opening_leave_balance:   data.opening_leave_balance   ?? 0,
        });
      });
  }, [open, member.id]);

  async function handleSave() {
    setErr(''); setSaving(true);
    const payload = {
      eitje_user_id:           fields.eitje_user_id?.toString().trim() || null,
      leave_accrual_pct:       parseFloat(fields.leave_accrual_pct) || 8.0,
      balance_calc_start_date: fields.balance_calc_start_date || null,
      opening_ot_balance:      parseFloat(fields.opening_ot_balance) || 0,
      opening_leave_balance:   parseFloat(fields.opening_leave_balance) || 0,
    };
    const { error } = await supabase.from('team_members').update(payload).eq('id', member.id);
    setSaving(false);
    if (error) { setErr('Save failed: ' + error.message); return; }
    setSaved(true); setTimeout(() => setSaved(false), 2500);
    if (onSaved) onSaved(payload);
  }

  function set(k, v) { setFields(p => ({ ...p, [k]: v })); setFindResult(null); }

  async function handleFind() {
    if (!member.name) return;
    setFinding(true); setFindResult(null);
    function norm(s) { return (s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^a-z0-9 ]/g,'').trim(); }
    const target = norm(member.name);
    const parts  = target.split(/\s+/).filter(Boolean);
    const { data } = await supabase.from('eitje_hours').select('eitje_user_id, user_name, date').not('eitje_user_id','is',null).order('date',{ascending:false}).limit(15000);
    if (!data) { setFinding(false); setFindResult({ found:false }); return; }
    const map = new Map();
    data.forEach(r => { if (!r.eitje_user_id) return; const k = String(r.eitje_user_id); if (!map.has(k)) map.set(k,{ eitje_user_id:k, user_name:r.user_name, last_seen:r.date }); });
    const users = Array.from(map.values());
    let hits = users.filter(u => norm(u.user_name) === target);
    if (!hits.length) hits = users.filter(u => norm(u.user_name).split(/\s+/).reverse().join(' ') === target);
    if (!hits.length && parts.length >= 2) hits = users.filter(u => { const n = norm(u.user_name).split(/\s+/); return parts.every(p => n.includes(p)); });
    if (!hits.length && parts[0]?.length >= 3) hits = users.filter(u => parts.every(p => norm(u.user_name).includes(p)));
    hits = hits.sort((a,b) => (b.last_seen||'').localeCompare(a.last_seen||''));
    if (hits.length === 1) { set('eitje_user_id', hits[0].eitje_user_id); setFindResult({ found:true, auto:true, match:hits[0] }); }
    else if (hits.length > 1) { setFindResult({ found:true, auto:false, matches:hits.slice(0,5) }); }
    else { setFindResult({ found:false }); }
    setFinding(false);
  }

  const isFT = member.employment_type === 'Full Time';

  return (
    <div style={{ background:C.white, borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'none', border:'none', cursor:'pointer' }}>
        <span style={{ fontSize:'11px', fontWeight:700, color:C.skyText, textTransform:'uppercase', letterSpacing:'0.05em' }}>Eitje & Balance Settings</span>
        {open ? <ChevronUp size={13} color={C.mid}/> : <ChevronDown size={13} color={C.mid}/>}
      </button>
      {open && (
        <div style={{ padding:'0 16px 16px', display:'flex', flexDirection:'column', gap:'12px' }}>
          {fields === null ? (
            <div style={{ fontSize:'13px', color:C.mid }}>Loading…</div>
          ) : (
            <>
              <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                {/* Eitje ID + Find */}
                <div>
                  <label style={flbl}>Eitje User ID</label>
                  <div style={{ display:'flex', gap:'6px' }}>
                    <input value={fields.eitje_user_id} onChange={e => set('eitje_user_id', e.target.value)} placeholder="e.g. 76370" style={{ ...finp, flex:1 }}/>
                    <button onClick={handleFind} disabled={finding} style={{ padding:'8px 14px', background:finding?C.border:C.skyText, color:C.white, border:'none', borderRadius:'8px', cursor:finding?'not-allowed':'pointer', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap' }}>
                      {finding ? '…' : '🔍 Find'}
                    </button>
                  </div>
                  {findResult?.found && findResult.auto && (
                    <div style={{ marginTop:'6px', padding:'6px 10px', background:C.greenLight, borderRadius:'6px', fontSize:'11px', color:C.green }}>
                      ✓ Matched: <strong>{findResult.match.user_name}</strong> · ID {findResult.match.eitje_user_id} · last shift {findResult.match.last_seen}
                    </div>
                  )}
                  {findResult?.found && !findResult.auto && (
                    <div style={{ marginTop:'6px', padding:'8px 10px', background:C.amberLight, borderRadius:'6px', border:`1px solid ${C.amber}33` }}>
                      <div style={{ fontSize:'11px', color:C.amber, fontWeight:600, marginBottom:'6px' }}>Multiple matches — pick one:</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                        {findResult.matches.map(u => (
                          <button key={u.eitje_user_id} onClick={() => { set('eitje_user_id', u.eitje_user_id); setFindResult({ found:true, auto:true, match:u }); }}
                            style={{ textAlign:'left', padding:'6px 10px', background:C.white, border:`1px solid ${C.border}`, borderRadius:'6px', cursor:'pointer', fontSize:'12px', color:C.charcoal }}>
                            <strong>{u.user_name}</strong> <span style={{ color:C.mid }}>· last {u.last_seen} · ID {u.eitje_user_id}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {findResult && !findResult.found && (
                    <div style={{ marginTop:'6px', padding:'6px 10px', background:C.redLight, borderRadius:'6px', fontSize:'11px', color:C.red }}>
                      No match found — enter ID manually.
                    </div>
                  )}
                </div>
                {/* Balance calc start */}
                <div>
                  <label style={flbl}>Balance Calc Start <span style={{ fontWeight:400, color:C.mid }}>(if not join date)</span></label>
                  <input type="date" value={fields.balance_calc_start_date} onChange={e => set('balance_calc_start_date', e.target.value)} style={finp}/>
                </div>
                {isFT && (
                  <>
                    <div>
                      <label style={flbl}>Leave Accrual % <span style={{ fontWeight:400, color:C.mid }}>(default 8.0)</span></label>
                      <input type="number" step="0.01" min="0" max="20" value={fields.leave_accrual_pct} onChange={e => set('leave_accrual_pct', e.target.value)} style={finp}/>
                    </div>
                    <div>
                      <label style={flbl}>Opening OT Balance <span style={{ fontWeight:400, color:C.mid }}>(carry-in hrs)</span></label>
                      <input type="number" step="0.01" value={fields.opening_ot_balance} onChange={e => set('opening_ot_balance', e.target.value)} style={finp}/>
                    </div>
                    <div>
                      <label style={flbl}>Opening Leave Balance <span style={{ fontWeight:400, color:C.mid }}>(carry-in hrs)</span></label>
                      <input type="number" step="0.01" value={fields.opening_leave_balance} onChange={e => set('opening_leave_balance', e.target.value)} style={finp}/>
                    </div>
                  </>
                )}
              </div>
              {err && <div style={{ fontSize:'12px', color:C.red, background:C.redLight, borderRadius:'8px', padding:'8px 12px' }}>{err}</div>}
              <button onClick={handleSave} disabled={saving} style={{ padding:'8px 20px', background:saved?C.green:saving?C.border:C.navy, color:C.white, border:'none', borderRadius:'8px', cursor:saving?'not-allowed':'pointer', fontWeight:600, fontSize:'13px', alignSelf:'flex-start' }}>
                {saved ? '✓ Saved' : saving ? 'Saving…' : 'Save Settings'}
              </button>
              {!saving && !saved && <div style={{ fontSize:'11px', color:C.mid }}>After saving, re-run the recalc in the Hours tab to apply updated balance settings.</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Renewal decision modal ────────────────────────────────────────────
function RenewalModal({ member, currentUser, onClose, onSaved, onOpenContracts }) {
  const [decision,  setDecision]  = useState(null); // 'renew' | 'not_renewing'
  const [duration,  setDuration]  = useState('6');
  const [startDate, setStartDate] = useState(member.contract_end_date || new Date().toISOString().split('T')[0]);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';

  function addMonthsISO(dateStr, months) {
    if (!dateStr || !months) return null;
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + parseInt(months));
    return d.toISOString().split('T')[0];
  }
  function fmtDisplay(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });
  }

  const newEndDate = startDate && duration ? addMonthsISO(startDate, duration) : null;

  async function handleSave() {
    setSaving(true); setError('');
    if (decision === 'not_renewing') {
      const { error: dbErr } = await supabase.from('team_members').update({
        not_renewing:    true,
        leaving_date:    member.contract_end_date || null,
        renewal_dismissed: true,
      }).eq('id', member.id);
      if (dbErr) { setError(dbErr.message); setSaving(false); return; }
      onSaved({ not_renewing: true, leaving_date: member.contract_end_date, renewal_dismissed: true });
    } else if (decision === 'renew') {
      // Update team_members with new contract dates
      const { error: dbErr } = await supabase.from('team_members').update({
        contract_start_date: startDate,
        contract_end_date:   newEndDate,
        contract_type:       'Fixed Term',
        renewal_dismissed:   true,
        not_renewing:        false,
      }).eq('id', member.id);
      if (dbErr) { setError(dbErr.message); setSaving(false); return; }
      onSaved({
        contract_start_date: startDate,
        contract_end_date:   newEndDate,
        contract_type:       'Fixed Term',
        renewal_dismissed:   true,
        not_renewing:        false,
      });
    }
    setSaving(false);
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:700, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.white, borderRadius:'16px', width:'100%', maxWidth:'440px', boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ padding:'18px 24px', background:C.navy, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'15px', color:C.white }}>Contract Renewal</div>
            <div style={{ fontSize:'12px', color:C.skyBlue, marginTop:'2px' }}>{member.name} · ends {fmtDisplay(member.contract_end_date)}</div>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.12)', border:'none', borderRadius:'8px', cursor:'pointer', color:C.white, padding:'6px', display:'flex' }}><X size={18}/></button>
        </div>

        <div style={{ padding:'24px', display:'flex', flexDirection:'column', gap:'16px' }}>

          {/* Decision buttons */}
          {!decision && (
            <>
              <div style={{ fontSize:'13px', color:C.mid, marginBottom:'4px' }}>What is the outcome for this contract?</div>
              <div style={{ display:'flex', gap:'10px' }}>
                <button onClick={() => setDecision('renew')}
                  style={{ flex:1, padding:'16px 12px', background:C.greenLight, border:`2px solid ${C.green}44`, borderRadius:'12px', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.green}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.green + '44'}>
                  <div style={{ fontSize:'1.5rem', marginBottom:'6px' }}>✅</div>
                  <div style={{ fontWeight:700, fontSize:'13px', color:C.green }}>Renew</div>
                  <div style={{ fontSize:'11px', color:C.mid, marginTop:'3px' }}>Set new contract dates</div>
                </button>
                <button onClick={() => setDecision('not_renewing')}
                  style={{ flex:1, padding:'16px 12px', background:C.redLight, border:`2px solid ${C.red}44`, borderRadius:'12px', cursor:'pointer', textAlign:'center', transition:'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.red}
                  onMouseLeave={e => e.currentTarget.style.borderColor = C.red + '44'}>
                  <div style={{ fontSize:'1.5rem', marginBottom:'6px' }}>🚪</div>
                  <div style={{ fontWeight:700, fontSize:'13px', color:C.red }}>Not renewing</div>
                  <div style={{ fontSize:'11px', color:C.mid, marginTop:'3px' }}>Mark as leaving</div>
                </button>
              </div>
            </>
          )}

          {/* Renew form */}
          {decision === 'renew' && (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div>
                  <label style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'4px' }}>New start date</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'13px', fontFamily:'inherit', boxSizing:'border-box' }}/>
                </div>
                <div>
                  <label style={{ fontSize:'10px', fontWeight:700, color:C.mid, textTransform:'uppercase', letterSpacing:'0.05em', display:'block', marginBottom:'4px' }}>Duration (months)</label>
                  <input type="number" min="1" max="36" value={duration} onChange={e => setDuration(e.target.value)} style={{ width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'13px', fontFamily:'inherit', boxSizing:'border-box' }}/>
                </div>
              </div>
              {newEndDate && (
                <div style={{ padding:'10px 14px', background:C.skyBlueLight, border:`1px solid ${C.skyBlue}`, borderRadius:'8px', fontSize:'13px', color:C.skyText, fontWeight:600 }}>
                  New end date: {fmtDisplay(newEndDate)}
                </div>
              )}
              <div style={{ fontSize:'11px', color:C.mid }}>This saves the new contract dates. {isAdmin ? 'Generate or log the contract document from the Contracts view.' : 'Admin can generate the contract document from the Contracts view.'}</div>
            </>
          )}

          {/* Not renewing confirmation */}
          {decision === 'not_renewing' && (
            <div style={{ padding:'14px', background:C.redLight, border:`1px solid ${C.red}44`, borderRadius:'10px' }}>
              <div style={{ fontWeight:700, fontSize:'13px', color:C.red, marginBottom:'6px' }}>Confirm: not renewing</div>
              <div style={{ fontSize:'12px', color:C.charcoal, lineHeight:1.5 }}>
                {member.name} will be marked as leaving on <strong>{fmtDisplay(member.contract_end_date)}</strong>. They stay on the roster as active until you manually set them to inactive.
              </div>
            </div>
          )}

          {error && <div style={{ padding:'10px', background:C.redLight, border:`1px solid ${C.red}44`, borderRadius:'8px', fontSize:'12px', color:C.red }}>{error}</div>}

          {decision && (
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={handleSave} disabled={saving || (decision === 'renew' && !newEndDate)}
                style={{ flex:1, padding:'10px', background:saving ? C.border : decision === 'renew' ? C.green : C.red, color:C.white, border:'none', borderRadius:'8px', cursor:saving ? 'not-allowed':'pointer', fontWeight:700, fontSize:'13px' }}>
                {saving ? 'Saving…' : decision === 'renew' ? '✅ Confirm renewal' : '🚪 Confirm not renewing'}
              </button>
              <button onClick={() => { setDecision(null); setError(''); }}
                style={{ padding:'10px 14px', background:C.white, border:`1px solid ${C.border}`, borderRadius:'8px', cursor:'pointer', fontSize:'13px', color:C.mid }}>
                Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Admin legacy onboarding override ─────────────────────────────────
function LegacyCompleteButton({ member, onDone }) {
  const [confirming, setConfirming] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  async function handleConfirm() {
    setSaving(true); setError('');
    const updates = { onboarding_pack_confirmed: true, onboarding_checklist_done: true, probation_done: true };
    const { error: dbErr } = await supabase
      .from('team_members')
      .update(updates)
      .eq('id', member.id);
    setSaving(false);
    if (dbErr) { setError('Error: ' + dbErr.message); return; }
    onDone({ ...member, ...updates });
  }

  if (confirming) {
    return (
      <div style={{ marginTop: '8px', padding: '12px 14px', background: C.amberLight, border: `1px solid ${C.amber}44`, borderRadius: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: C.amber, marginBottom: '6px' }}>
          Mark onboarding complete for {member.name}?
        </div>
        <div style={{ fontSize: '11px', color: C.charcoal, marginBottom: '10px', lineHeight: 1.5 }}>
          This bypasses all step checks. Use for existing team members who completed onboarding before Lemon Tree.
        </div>
        {error && <div style={{ fontSize: '11px', color: C.red, marginBottom: '8px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleConfirm} disabled={saving}
            style={{ flex: 1, padding: '8px', background: saving ? C.border : C.green, color: C.white, border: 'none', borderRadius: '7px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 700 }}>
            {saving ? 'Saving…' : '✓ Yes, mark complete'}
          </button>
          <button onClick={() => { setConfirming(false); setError(''); }}
            style={{ padding: '8px 14px', background: C.white, border: `1px solid ${C.border}`, borderRadius: '7px', cursor: 'pointer', fontSize: '12px', color: C.mid }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={() => setConfirming(true)}
      style={{ width: '100%', marginTop: '6px', padding: '8px', background: C.cream, color: C.mid, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
      ✓ Mark onboarding complete (legacy / admin override)
    </button>
  );
}

// ── Leave Requests card ───────────────────────────────────────────────
function LeaveRequestsCard({ requests, loading, expanded, onToggle }) {
  const today = new Date().toISOString().slice(0, 10);

  const onLeaveNow = requests.find(r =>
    r.status === 'accepted' &&
    r.start_datetime.slice(0, 10) <= today &&
    r.end_datetime.slice(0, 10) >= today
  );
  const pending  = requests.filter(r => r.status === 'pending');
  const accepted = requests.filter(r => r.status === 'accepted');

  const maxAge = pending.length > 0
    ? Math.max(...pending.map(r => Math.floor((Date.now() - new Date(r.created_at)) / 86400000)))
    : 0;

  const cardColor = onLeaveNow
    ? { bg: C.greenLight,    border: C.green + '44',    text: C.green,   icon: C.green   }
    : maxAge >= 14
    ? { bg: C.redLight,      border: C.red + '44',      text: C.red,     icon: C.red     }
    : maxAge >= 10
    ? { bg: C.amberLight,    border: C.amber + '44',    text: C.amber,   icon: C.amber   }
    : { bg: C.purpleLight, border: '#C4B5FD', text: C.purple, icon: C.purple };

  function fmtShort(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  const ST = {
    accepted: { bg: C.greenLight, border: C.green, text: C.green, label: 'Approved' },
    pending:  { bg: C.amberLight, border: C.amber, text: C.amber, label: 'Pending'  },
    denied:   { bg: C.redLight,   border: C.red,   text: C.red,   label: 'Denied'   },
  };

  return (
    <div
      onClick={onToggle}
      style={{ borderRadius: '12px', border: `1px solid ${cardColor.border}`, background: cardColor.bg, padding: '14px 18px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Summary row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Calendar size={15} color={cardColor.icon} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: cardColor.icon, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px' }}>Leave Requests</div>
          {loading ? (
            <div style={{ fontSize: '13px', color: C.mid }}>Loading…</div>
          ) : onLeaveNow ? (
            <div style={{ fontSize: '14px', fontWeight: 800, color: cardColor.text }}>
              Currently on leave
              <span style={{ fontSize: '11px', fontWeight: 500, color: C.green, marginLeft: '8px' }}>back {fmtShort(onLeaveNow.end_datetime)}</span>
            </div>
          ) : requests.length === 0 ? (
            <div style={{ fontSize: '13px', color: C.mid }}>No leave requests</div>
          ) : (
            <div style={{ fontSize: '13px', fontWeight: 700, color: cardColor.text }}>
              {pending.length > 0 && <span>{pending.length} pending</span>}
              {pending.length > 0 && accepted.length > 0 && <span style={{ color: C.mid, fontWeight: 400, margin: '0 6px' }}>·</span>}
              {accepted.length > 0 && <span>{accepted.length} approved</span>}
              {pending.length === 0 && accepted.length === 0 && <span style={{ color: C.mid, fontWeight: 400 }}>No active requests</span>}
            </div>
          )}
        </div>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
          <path d="M5 2.5L9.5 7L5 11.5" stroke={cardColor.icon} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Expanded list */}
      {expanded && requests.length > 0 && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: `1px solid ${cardColor.border}`, display: 'flex', flexDirection: 'column', gap: '6px' }} onClick={e => e.stopPropagation()}>
          {requests.filter(r => r.status !== 'denied').map(r => {
            const st  = ST[r.status] || ST.pending;
            const dur = Math.max(1, Math.ceil((new Date(r.end_datetime) - new Date(r.start_datetime)) / 86400000));
            const age = r.status === 'pending' ? Math.floor((Date.now() - new Date(r.created_at)) / 86400000) : null;
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', background: C.white, borderRadius: '8px', border: `1px solid ${st.border}33` }}>
                <span style={{ padding: '2px 8px', borderRadius: '20px', background: st.bg, color: st.text, fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{st.label}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: C.charcoal }}>
                    {fmtShort(r.start_datetime)} – {fmtShort(r.end_datetime)}
                    <span style={{ fontWeight: 400, color: C.mid, marginLeft: '6px' }}>{dur}d</span>
                  </div>
                  {r.reason && <div style={{ fontSize: '11px', color: C.mid, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</div>}
                </div>
                {age !== null && age >= 10 && (
                  <span style={{ fontSize: '10px', fontWeight: 700, color: age >= 14 ? C.red : C.amber, flexShrink: 0 }}>
                    {age >= 14 ? '⚠ ' : ''}{age}d
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MemberModal({ member, onClose, onUpdate, currentUser, trainings, completions, onEdit }) {
  const isMgr    = currentUser?.role?.toLowerCase() === 'admin' || currentUser?.role === 'manager' || currentUser?.role === 'supervisor';
  const isAdmin  = currentUser?.role?.toLowerCase() === 'admin';
  const isMobile = window.innerWidth < 768;

  const [noteText,        setNoteText]        = useState('');
  const [saving,          setSaving]          = useState(false);
  const [local,           setLocal]           = useState(member);
  const [showContract,    setShowContract]    = useState(false);
  const [showChecklist,   setShowChecklist]   = useState(false);
  const [showOnboarding,  setShowOnboarding]  = useState(false);
  const [showEvaluation,  setShowEvaluation]  = useState(false);
  const [showEvalHistory, setShowEvalHistory] = useState(false);
  const [showWarnings,    setShowWarnings]    = useState(false);
  const [warningCount,    setWarningCount]    = useState(null);
  const [formalWarningCount, setFormalWarningCount] = useState(0);
  const [evalCount,       setEvalCount]       = useState(null);
  const [lastEvalDate,    setLastEvalDate]    = useState(null);
  const [packConfirmed,   setPackConfirmed]   = useState(!!member.onboarding_pack_confirmed);
  const [showNotes,         setShowNotes]         = useState(false);
  const [showSensitive,     setShowSensitive]     = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showDetailsOpen,   setShowDetailsOpen]   = useState(!isMobile);
  const [actionsOpen,       setActionsOpen]       = useState(false);
  const [contractCount,     setContractCount]     = useState(null);
  const [showPulseEval,     setShowPulseEval]     = useState(false);
  const [showRenewal,       setShowRenewal]       = useState(false);
  const [showHoursModal,    setShowHoursModal]    = useState(false);

  // Leave requests (Eitje — loaded on mount)
  const [leaveRequests,  setLeaveRequests]  = useState([]);
  const [leaveLoading,   setLeaveLoading]   = useState(false);
  const [leaveExpanded,  setLeaveExpanded]  = useState(false);

  // Lazy-loaded leave/OT
  const [leaveOtSnapshots, setLeaveOtSnapshots] = useState([]);
  const [leaveOtEntries,   setLeaveOtEntries]   = useState([]);
  const [leaveOtLoading,   setLeaveOtLoading]   = useState(false);

  useEffect(() => { setLocal(member); }, [member]);

  useEffect(() => {
    if (member.employment_type !== 'Full Time') return;
    let cancelled = false;
    async function load() {
      setLeaveOtLoading(true);
      const [{ data: snaps }, { data: ents }] = await Promise.all([
        supabase.from('leave_ot_snapshots').select('*').order('year', { ascending: false }).order('month', { ascending: false }),
        supabase.from('leave_ot_entries').select('*').eq('team_member_id', member.id),
      ]);
      if (cancelled) return;
      setLeaveOtSnapshots(snaps || []);
      setLeaveOtEntries(ents || []);
      setLeaveOtLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [member.id, member.employment_type]);

  // Fetch this member's leave requests from Eitje on mount (background)
  useEffect(() => {
    if (!member.eitje_user_id) return;
    let cancelled = false;
    async function loadLeave() {
      setLeaveLoading(true);
      const { data: credRows } = await supabase
        .from('app_settings').select('key,value')
        .in('key', ['eitje_partner_username','eitje_partner_password','eitje_api_username','eitje_api_password']);
      if (!credRows || credRows.length < 4 || cancelled) { setLeaveLoading(false); return; }
      const c = {};
      credRows.forEach(r => { c[r.key] = r.value; });
      const headers = {
        'Content-Type':     'application/json',
        'Partner-Username': c.eitje_partner_username,
        'Partner-Password': c.eitje_partner_password,
        'Api-Username':     c.eitje_api_username,
        'Api-Password':     c.eitje_api_password,
      };
      function isoDate(d) { return d.toISOString().slice(0, 10); }
      function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
      const now = new Date();
      const s1 = isoDate(now),             e1 = isoDate(addDays(now, 89));
      const s2 = isoDate(addDays(now, 90)), e2 = isoDate(addDays(now, 179));
      const s3 = isoDate(addDays(now, 180)), e3 = isoDate(addDays(now, 269));
      try {
        const [r1, r2, r3] = await Promise.all([
          fetch(`https://open-api.eitje.app/open_api/leave_requests?filters[start_date]=${s1}&filters[end_date]=${e1}`, { method: 'GET', headers }),
          fetch(`https://open-api.eitje.app/open_api/leave_requests?filters[start_date]=${s2}&filters[end_date]=${e2}`, { method: 'GET', headers }),
          fetch(`https://open-api.eitje.app/open_api/leave_requests?filters[start_date]=${s3}&filters[end_date]=${e3}`, { method: 'GET', headers }),
        ]);
        const [t1, t2, t3] = await Promise.all([r1.text(), r2.text(), r3.text()]);
        const j1 = t1 ? JSON.parse(t1) : {};
        const j2 = t2 ? JSON.parse(t2) : {};
        const j3 = t3 ? JSON.parse(t3) : {};
        const all = [...(j1.items || []), ...(j2.items || []), ...(j3.items || [])];
        const uid = String(member.eitje_user_id);
        const mine = all.filter(r => String(r.user?.id) === uid || String(r.user_id) === uid);
        const seen = new Set();
        const deduped = mine.filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
        deduped.sort((a, b) => b.start_datetime.localeCompare(a.start_datetime));
        if (!cancelled) setLeaveRequests(deduped);
      } catch (e) { /* silently fail — non-critical */ }
      if (!cancelled) setLeaveLoading(false);
    }
    loadLeave();
    return () => { cancelled = true; };
  }, [member.id, member.eitje_user_id]);

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('onboarding_invites').select('completed_at').eq('member_id', member.id).eq('type', 'pack').not('completed_at', 'is', null).maybeSingle();
      if (data?.completed_at && !member.onboarding_pack_confirmed) {
        await supabase.from('team_members').update({ onboarding_pack_confirmed: true }).eq('id', member.id);
        setPackConfirmed(true);
      }
    }
    load();
  }, [member.id]);

  useEffect(() => {
    supabase.from('evaluations').select('id, date').eq('team_member_id', member.id).order('date', { ascending: false })
      .then(({ data }) => { if (data) { setEvalCount(data.length); setLastEvalDate(data[0]?.date || null); } });
  }, [member.id]);

  useEffect(() => {
    supabase.from('warnings').select('id, internal_only').eq('team_member_id', member.id)
      .then(({ data }) => { if (data) { setWarningCount(data.length); setFormalWarningCount(data.filter(w => !w.internal_only).length); } });
  }, [member.id]);

  useEffect(() => {
    supabase.from('contracts').select('id', { count: 'exact' }).eq('team_member_id', member.id)
      .then(({ count }) => { setContractCount(count || 0); });
  }, [member.id]);

  // Derived
  const notes      = local.notes_log || [];
  const skillList  = local.dept === 'KIT' ? KIT_SKILLS : BAR_SKILLS;
  const now = new Date();
  const latestSnap = leaveOtSnapshots?.find(s =>
    s.year < now.getFullYear() ||
    (s.year === now.getFullYear() && s.month < (now.getMonth() + 1))
  );
  const latestEntry = latestSnap ? leaveOtEntries?.find(e => e.snapshot_id === latestSnap.id) : null;

  // 4-week rolling average — sum last 28 days ÷ weeks with data (handles new TMs with < 4 weeks)
  const [rollingAvgWeekly, setRollingAvgWeekly] = useState(null);
  useEffect(() => {
    if (!local.eitje_user_id) return;
    async function loadRolling() {
      // toDate = last completed Sunday; if today is Sun go back 7 to avoid partial week
      // Use noon to prevent toISOString() rolling back a day in UTC+x timezones
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      const daysToLastSun = today.getDay() === 0 ? 7 : today.getDay();
      const toDate = new Date(today);
      toDate.setDate(today.getDate() - daysToLastSun);
      const fromDate = new Date(toDate);
      fromDate.setDate(toDate.getDate() - 27); // 28 days total

      // Format as YYYY-MM-DD using local date components (not UTC) to avoid timezone shift
      function toLocalISO(d) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }

      const fromIso = toLocalISO(fromDate);
      const toIso   = toLocalISO(toDate);

      const { data } = await supabase
        .from('eitje_hours')
        .select('date, type, hours_worked')
        .eq('eitje_user_id', local.eitje_user_id)
        .in('type', ['gewerkte_uren', 'verlof', 'ziek', 'bijzonder_verlof'])
        .gte('date', fromIso)
        .lte('date', toIso);

      if (!data || data.length === 0) { setRollingAvgWeekly(null); return; }

      // Helper: get Mon-start week key for a date string
      // Parse as local date (not UTC) to avoid day-of-week being wrong in UTC+x timezones
      function weekKey(dateStr) {
        const [y, m, day] = dateStr.split('-').map(Number);
        const d = new Date(y, m - 1, day); // local midnight — no timezone shift
        const dow = d.getDay() === 0 ? 6 : d.getDay() - 1; // 0=Mon
        return toLocalISO(new Date(y, m - 1, day - dow));
      }

      const weeksWithAnyData  = new Set(); // any type — denominator for weekly avg
      const weeksWithShifts   = new Set(); // gewerkte_uren — denominator for shift avg
      const shiftDays         = new Set(); // distinct days worked — for avg shift length
      let total = 0;                       // all types summed
      let totalWorked = 0;                 // gewerkte_uren only

      for (const row of data) {
        const hrs = parseFloat(row.hours_worked) || 0;
        if (hrs <= 0) continue;
        total += hrs;
        weeksWithAnyData.add(weekKey(row.date));
        if (row.type === 'gewerkte_uren') {
          totalWorked += hrs;
          shiftDays.add(row.date);
          weeksWithShifts.add(weekKey(row.date));
        }
      }

      const weekCount      = weeksWithAnyData.size;
      const shiftWeekCount = weeksWithShifts.size;
      if (weekCount === 0) { setRollingAvgWeekly(null); return; }

      const avg      = Math.round((total / weekCount) * 10) / 10;
      const avgShift = shiftDays.size > 0
        ? Math.round((totalWorked / shiftDays.size) * 10) / 10
        : null;
      // Avg shifts per week: shifts worked ÷ weeks with any data (for header badge)
      const avgShiftsPerWeek = weekCount > 0
        ? Math.round((shiftDays.size / weekCount) * 10) / 10
        : null;

      setRollingAvgWeekly({ avg, weekCount, avgShift, shiftCount: shiftDays.size, avgShiftsPerWeek });
    }
    loadRolling();
  }, [local.eitje_user_id]);
  const mTrainings = trainings.map(t => {
    const app  = t.dept === 'All' || t.dept === local.dept;
    const done = app && completions.some(c => c.training_id === t.id && c.team_member_id === local.id);
    return { ...t, app, done };
  }).filter(t => t.app);
  const tDone = mTrainings.filter(t => t.done).length;

  // Onboarding completeness — step 3 is first evaluation done (replaces quiz)
  const evalDone = evalCount !== null && evalCount > 0;
  const onbSteps = [packConfirmed, local.onboarding_checklist_done, evalDone, local.probation_done];
  const onbDone  = onbSteps.filter(Boolean).length;
  const onbComplete = onbDone === 4;

  // Contract status
  const contractDays = local.contract_type === 'Permanent' ? null : daysUntil(local.contract_end_date);
  const contractStatus = local.contract_type === 'Permanent' ? 'good'
    : contractDays === null ? 'neutral'
    : contractDays < 0  ? 'alert'
    : contractDays < 30 ? 'alert'
    : contractDays < 60 ? 'warn'
    : 'good';
  const contractValue = local.contract_type === 'Permanent' ? 'Permanent'
    : contractDays === null ? '—'
    : contractDays < 0  ? 'Expired'
    : `${contractDays}d left`;

  // Hours status
  const otBal    = latestEntry ? parseFloat(latestEntry.ot_balance) : null;
  const lvBal    = latestEntry ? parseFloat(latestEntry.leave_balance) : null;
  const combined = otBal !== null && lvBal !== null ? Math.round((otBal + lvBal) * 10) / 10 : null;

  async function addNote() {
    if (!noteText.trim()) return;
    setSaving(true);
    const n = { text: noteText.trim(), author: currentUser?.name || 'Manager', created_at: new Date().toISOString() };
    const log = [...notes, n];
    const { error } = await supabase.from('team_members').update({ notes_log: log }).eq('id', local.id);
    if (!error) { const u = { ...local, notes_log: log }; setLocal(u); onUpdate(u); setNoteText(''); }
    setSaving(false);
  }

  async function toggleCheck(field) {
    if (!isMgr) return;
    const v = !local[field];
    const { error } = await supabase.from('team_members').update({ [field]: v }).eq('id', local.id);
    if (!error) { const u = { ...local, [field]: v }; setLocal(u); onUpdate(u); }
  }

  async function toggleTraining(tid) {
    if (!isMgr) return;
    const ex = completions.find(c => c.training_id === tid && c.team_member_id === local.id);
    if (ex) await supabase.from('training_completions').delete().eq('id', ex.id);
    else    await supabase.from('training_completions').insert([{ training_id: tid, team_member_id: local.id, completed_by: currentUser?.name || 'Manager' }]);
    onUpdate({ ...local, _refreshCompletions: true });
  }

  const btnStyle = { display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', cursor: 'pointer', color: C.white, fontSize: '12px', fontWeight: 600 };
  const fieldStyle = { fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '3px' };
  const valStyle   = { fontSize: '13px', fontWeight: 600, color: C.charcoal };

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={onClose}>
        <div style={{ background: C.cream, borderRadius: '16px', width: '100%', maxWidth: isMobile ? '100%' : '820px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }} onClick={e => e.stopPropagation()}>

          {/* ── HEADER ────────────────────────────────────────────── */}
          <div style={{ background: C.navy, borderRadius: '16px 16px 0 0', position: 'sticky', top: 0, zIndex: 10 }}>
            <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <RoleIcon dept={local.dept} size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 800, fontSize: '17px', color: C.white }}>{local.name}</span>
                  {isNew(local) && <span style={{ padding: '2px 8px', borderRadius: '20px', background: C.yellow, color: C.charcoal, fontSize: '10px', fontWeight: 700 }}>NEW</span>}
                </div>
                <div style={{ fontSize: '13px', color: C.skyBlue, marginTop: '3px' }}>{local.role || local.dept} · {local.venue}</div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: local.contract_type === 'Permanent' ? C.greenLight : C.amberLight, color: local.contract_type === 'Permanent' ? C.green : C.amber }}>
                    {local.contract_type === 'Permanent' ? 'PERMANENT' : 'FIXED TERM'}
                  </span>
                  <span style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: 'rgba(255,255,255,0.12)', color: C.skyBlue }}>
                    {local.employment_type === 'Full Time' ? `FT · ${local.contract_hours}hr/wk` : `PT${rollingAvgWeekly?.avgShiftsPerWeek != null ? ` · ${rollingAvgWeekly.avgShiftsPerWeek} shifts/wk` : ''}`}
                  </span>
                </div>
              </div>

              {/* Icon buttons — pencil + X */}
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                {isMgr && (
                  <button onClick={() => { onClose(); onEdit(local); }} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '8px', display: 'flex' }} title="Edit">
                    <Edit2 size={16} />
                  </button>
                )}
                <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '8px', display: 'flex' }}><X size={16} /></button>
              </div>
            </div>
          </div>

          {/* ── BODY ──────────────────────────────────────────────── */}
          <div style={{ padding: isMobile ? '14px' : '20px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px', alignItems: 'flex-start' }}>

            {/* ── LEFT: Identity card ── */}
            <div style={{ width: isMobile ? '100%' : '240px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>

              {/* Key details — collapsible */}
              <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                <button
                  onClick={() => setShowDetailsOpen(o => !o)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>TM Details</span>
                  {showDetailsOpen ? <ChevronUp size={13} color={C.mid}/> : <ChevronDown size={13} color={C.mid}/>}
                </button>
                {showDetailsOpen && (
                  <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[
                      ['Joined',         fmt(local.join_date)],
                      ['Contract start', fmt(local.contract_start_date)],
                      ['Contract ends',  local.contract_type === 'Permanent' ? '—' : fmt(local.contract_end_date)],
                      ['Contract #',     `#${local.contract_number || '—'}`],
                      ['Birthday',       fmt(local.birthday)],
                      ['Email',          local.email || '—'],
                      ['Phone',          local.phone || '—'],
                    ].map(([l, v]) => (
                      <div key={l}>
                        <div style={fieldStyle}>{l}</div>
                        <div style={{ ...valStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v}>{v}</div>
                      </div>
                    ))}
                    {/* Skills nested inside details */}
                    {local.skills?.length > 0 && (
                      <div style={{ paddingTop: '10px', borderTop: `1px solid ${C.border}`, marginTop: '2px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Skills</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {local.skills.map(s => (
                            <span key={s} style={{ padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: C.skyBlueLight, color: C.skyText, border: `1px solid ${C.skyBlue}44` }}>{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Sensitive — admin only, desktop only (mobile renders below notes) */}
              {isAdmin && !isMobile && (
                <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.red}33`, overflow: 'hidden' }}>
                  <button onClick={() => setShowSensitive(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: C.red, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sensitive</span>
                    {showSensitive ? <ChevronUp size={13} color={C.red} /> : <ChevronDown size={13} color={C.red} />}
                  </button>
                  {showSensitive && (
                    <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {[['BSN', local.bsn || '—'], ['IBAN', local.iban || '—'], ['Nationality', local.nationality || '—']].map(([l, v]) => (
                        <div key={l}><div style={fieldStyle}>{l}</div><div style={valStyle}>{v}</div></div>
                      ))}
                      <div>
                        <div style={fieldStyle}>{local.employment_type === 'Full Time' ? 'Monthly salary' : 'Hourly rate'}</div>
                        <div style={valStyle}>
                          {local.employment_type === 'Full Time'
                            ? (local.gross_monthly_salary ? `€${parseFloat(local.gross_monthly_salary).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}` : '—')
                            : (local.hourly_rate ? `€${parseFloat(local.hourly_rate).toFixed(2)}/hr` : '—')}
                        </div>
                      </div>
                      {(local.id_doc_path || local.bsn_doc_path) && (
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                          {local.id_doc_path  && <MemberDocButton label='🪪 ID' path={local.id_doc_path} />}
                          {local.bsn_doc_path && <MemberDocButton label='📄 BSN' path={local.bsn_doc_path} />}
                        </div>
                      )}
                      <div style={{ fontSize: '10px', color: C.mid, fontStyle: 'italic' }}>Do not share or screenshot</div>
                    </div>
                  )}
                </div>
              )}

              {/* Eitje & Balance Settings — admin only, desktop only */}
              {isAdmin && !isMobile && (
                <EitjeBalanceSettings
                  member={local}
                  currentUser={currentUser}
                  onSaved={payload => { const u = { ...local, ...payload }; setLocal(u); onUpdate(u); }}
                />
              )}

              {/* Manager Notes — desktop left col only */}
              {!isMobile && (
                <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                  <button onClick={() => setShowNotes(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileText size={13} color={C.mid} />
                      <span style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manager Notes</span>
                      {notes.length > 0 && <span style={{ padding: '1px 7px', borderRadius: '20px', background: C.cream, color: C.mid, fontSize: '11px', fontWeight: 600, border: `1px solid ${C.border}` }}>{notes.length}</span>}
                    </div>
                    {showNotes ? <ChevronUp size={14} color={C.mid} /> : <ChevronDown size={14} color={C.mid} />}
                  </button>
                  {showNotes && (
                    <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {notes.length === 0 ? (
                        <div style={{ fontSize: '13px', color: C.mid, fontStyle: 'italic' }}>No notes yet</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {[...notes].reverse().map((n, i) => (
                            <div key={i} style={{ padding: '10px 12px', background: '#FEFCE8', borderRadius: '8px', border: '1px solid #F7EC71' }}>
                              <div style={{ fontSize: '11px', color: C.mid, marginBottom: '4px' }}>{n.author} · {fmtDT(n.created_at)}</div>
                              <div style={{ fontSize: '13px', color: C.charcoal, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {isMgr && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note…" rows={2} style={{ ...inp, flex: 1, resize: 'vertical', fontFamily: 'inherit' }} />
                          <button onClick={addNote} disabled={saving || !noteText.trim()} style={{ padding: '8px 16px', background: saving || !noteText.trim() ? C.border : C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: saving || !noteText.trim() ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {saving ? '…' : 'Add'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── RIGHT: Dashboard ── */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>

              {/* Status tile grid — 2 cols × 3 rows */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>

                {/* Onboarding tile */}
                <Tile
                  icon={<CheckCircle size={15} />}
                  label="Onboarding"
                  value={onbComplete ? '✓ Complete' : `${onbDone} / 4 done`}
                  sub={onbComplete ? `NMBRS ${local.nmbrs_profile_done ? '✓' : '✗'} · Contract ${local.contract_signed ? '✓' : '✗'} · Deposit ${local.uniform_deposit_paid ? '✓' : '✗'}` : 'Pack · Checklist · Quiz · Sign-off'}
                  status={onbComplete ? 'good' : onbDone >= 2 ? 'warn' : 'alert'}
                  onClick={isMgr ? () => setShowOnboardingModal(true) : undefined}
                  actionLabel={isMgr ? 'Manage' : undefined}
                />

                {/* Contract tile */}
                <Tile
                  icon={<FileText size={15} />}
                  label="Contract"
                  value={local.not_renewing ? 'Not renewing' : contractValue}
                  sub={contractCount !== null && contractCount > 0
                    ? `Contract #${contractCount} · ${local.contract_type === 'Permanent' ? `Since ${fmt(local.contract_start_date)}` : `Ends ${fmt(local.contract_end_date)}`}`
                    : local.contract_type === 'Permanent' ? `Since ${fmt(local.contract_start_date)}` : `Ends ${fmt(local.contract_end_date)}`}
                  status={local.not_renewing ? 'alert' : contractStatus}
                  onClick={isAdmin || isMgr ? () => (local.not_renewing || contractStatus === 'alert' || contractStatus === 'warn' ? setShowRenewal(true) : setShowContract(true)) : undefined}
                  actionLabel={local.not_renewing ? 'Review decision' : (contractStatus === 'alert' || contractStatus === 'warn') ? 'Renewal decision' : isAdmin ? 'View contracts' : undefined}
                />

                {/* Evaluations tile */}
                <Tile
                  icon={<Award size={15} />}
                  label="Evaluations"
                  value={evalCount === null ? '…' : evalCount === 0 ? 'None yet' : `${evalCount} done`}
                  sub={lastEvalDate ? `Last: ${fmt(lastEvalDate)}` : isMgr ? 'Start first evaluation' : 'No evaluations yet'}
                  status={evalCount === null ? 'neutral' : evalCount === 0 ? 'warn' : 'good'}
                  onClick={isMgr ? () => (evalCount === 0 ? setShowEvaluation(true) : setShowEvalHistory(true)) : undefined}
                  actionLabel={isMgr ? (evalCount === 0 ? 'Start' : 'View all') : undefined}
                />

                {/* Conduct tile */}
                <Tile
                  icon={<Shield size={15} />}
                  label="Record"
                  value={warningCount === null ? '…' : warningCount === 0 ? 'All good' : `${warningCount} ${warningCount === 1 ? 'entry' : 'entries'}`}
                  sub={formalWarningCount > 0 ? `${formalWarningCount} formal warning${formalWarningCount > 1 ? 's' : ''}` : warningCount > 0 ? 'Internal notes only' : 'No issues on record'}
                  status={warningCount === null ? 'neutral' : formalWarningCount > 0 ? 'alert' : warningCount > 0 ? 'warn' : 'good'}
                  onClick={isMgr ? () => setShowWarnings(true) : undefined}
                  actionLabel={isMgr ? (warningCount === 0 ? 'Add note' : 'View log') : undefined}
                />

                {/* Training tile */}
                <Tile
                  icon={<ClipboardList size={15} />}
                  label="Training"
                  value={mTrainings.length === 0 ? 'None assigned' : `${tDone} / ${mTrainings.length}`}
                  sub={mTrainings.length > 0 ? (tDone === mTrainings.length ? 'All complete' : `${mTrainings.length - tDone} remaining`) : undefined}
                  status={mTrainings.length === 0 ? 'neutral' : tDone === mTrainings.length ? 'good' : mTrainings.some(t => t.required && !t.done) ? 'alert' : 'warn'}
                  onClick={isMgr && mTrainings.length > 0 ? () => setShowTrainingModal(true) : undefined}
                  actionLabel={isMgr && mTrainings.length > 0 ? 'View' : undefined}
                />

                {/* Check-In — tile 6, all TMs */}
                <Tile
                  icon={<Clock size={15} />}
                  label="Check-In"
                  value={local.pulse_dismissed ? 'Done ✓' : fmt(midDate(local.contract_start_date, local.contract_end_date, local.contract_type)) || '—'}
                  sub={local.pulse_dismissed ? 'Check-in completed' : local.contract_type === 'Permanent' ? '6-month check-in' : 'Mid-contract check-in'}
                  status={local.pulse_dismissed ? 'good' : 'info'}
                  onClick={isMgr && !local.pulse_dismissed ? () => setShowPulseEval(true) : undefined}
                  actionLabel={isMgr && !local.pulse_dismissed ? 'Start check-in' : undefined}
                />
              </div>

              {/* FT leave/hours banner — full width below grid */}
              {local.employment_type === 'Full Time' && (() => {
                const bannerColor = combined === null ? C.skyText
                  : combined < -15 ? C.red
                  : combined < -1  ? C.amber
                  : C.skyText;
                const bannerBg = combined === null ? C.skyBlueLight
                  : combined < -15 ? C.redLight
                  : combined < -1  ? C.amberLight
                  : C.skyBlueLight;
                const bannerBorder = combined === null ? C.skyBlue + '44'
                  : combined < -15 ? C.red + '44'
                  : combined < -1  ? C.amber + '44'
                  : C.skyBlue + '44';
                return (
                  <div
                    onClick={isMgr ? () => setShowHoursModal(true) : undefined}
                    style={{ borderRadius: '12px', border: `1px solid ${bannerBorder}`, background: bannerBg, padding: '14px 18px', cursor: isMgr ? 'pointer' : 'default', display: 'flex', alignItems: 'stretch', transition: 'box-shadow 0.15s' }}
                    onMouseEnter={e => { if (isMgr) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {/* Row 1: Hours balance · OT · Leave */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                        <TrendingUp size={15} color={bannerColor} style={{ flexShrink: 0, marginRight: '10px' }} />
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: bannerColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hours balance</div>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: '2px' }}>
                            <span style={{ fontSize: '15px', fontWeight: 800, color: C.charcoal }}>
                              {leaveOtLoading ? '…' : combined !== null ? `${combined > 0 ? '+' : ''}${combined}h` : '—'}
                            </span>
                            {latestSnap && (
                              <span style={{ fontSize: '11px', fontWeight: 500, color: C.mid }}>
                                {MONTHS[latestSnap.month - 1]} {new Date(latestSnap.year, latestSnap.month - 1, 0).getDate()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ width: '1px', height: '32px', background: bannerColor + '44', flexShrink: 0, margin: '0 14px' }}/>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: bannerColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>OT</div>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: C.charcoal, marginTop: '2px' }}>
                            {otBal !== null ? `${otBal > 0 ? '+' : ''}${otBal}h` : '—'}
                          </div>
                        </div>
                        <div style={{ width: '1px', height: '32px', background: bannerColor + '44', flexShrink: 0, margin: '0 14px' }}/>
                        <div>
                          <div style={{ fontSize: '10px', fontWeight: 700, color: bannerColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Leave</div>
                          <div style={{ fontSize: '14px', fontWeight: 700, color: C.charcoal, marginTop: '2px' }}>
                            {lvBal !== null ? `${lvBal}h` : '—'}
                          </div>
                        </div>
                        {!latestEntry && !leaveOtLoading && (
                          <div style={{ fontSize: '12px', color: C.mid, fontStyle: 'italic', marginLeft: '10px' }}>No data yet — run recalc in Hours tab</div>
                        )}
                        {isMgr && <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginLeft: 'auto' }}><path d="M5 2.5L9.5 7L5 11.5" stroke={bannerColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                      {/* Row 2: Avg last 4 weeks — always its own row */}
                      {rollingAvgWeekly !== null && (() => {
                        const { avg, weekCount } = rollingAvgWeekly;
                        const contractH = parseFloat(local.contract_hours) || 0;
                        const delta = Math.round((avg - contractH) * 10) / 10;
                        const avgColor = Math.abs(delta) <= 1 ? C.green
                          : delta < -5  ? C.red
                          : delta > 1   ? C.amber
                          : C.amber;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '8px', borderTop: `1px solid ${bannerColor}22` }}>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: bannerColor, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
                              Avg last {weekCount < 4 ? `${weekCount}` : '4'} weeks
                              {weekCount < 4 && <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: '3px' }}>({weekCount} of 4)</span>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginLeft: '8px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: avgColor }}>{avg}h/wk</span>
                              <span style={{ fontSize: '10px', fontWeight: 600, color: avgColor }}>({delta > 0 ? '+' : ''}{delta} vs {contractH}h)</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* PT avg hours strip */}
              {local.employment_type !== 'Full Time' && rollingAvgWeekly !== null && (
                <div style={{ borderRadius: '12px', border: `1px solid ${C.skyBlue}44`, background: C.skyBlueLight, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TrendingUp size={14} color={C.skyText} style={{ flexShrink: 0 }}/>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Avg last {rollingAvgWeekly.weekCount < 4 ? `${rollingAvgWeekly.weekCount}` : '4'} weeks
                        {rollingAvgWeekly.weekCount < 4 && <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: '3px' }}>({rollingAvgWeekly.weekCount} of 4)</span>}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: C.charcoal, marginTop: '1px' }}>{rollingAvgWeekly.avg}h/wk</div>
                    </div>
                  </div>
                  {rollingAvgWeekly.avgShift !== null && (
                    <>
                      <div style={{ width: '1px', height: '32px', background: C.skyBlue + '66', flexShrink: 0 }}/>
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: C.skyText, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Avg shift</div>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: C.charcoal, marginTop: '1px' }}>
                          {rollingAvgWeekly.avgShift}h
                          <span style={{ fontSize: '11px', fontWeight: 400, color: C.mid, marginLeft: '4px' }}>over {rollingAvgWeekly.shiftCount} shifts</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              {/* Leave Requests card */}
              <LeaveRequestsCard
                requests={leaveRequests}
                loading={leaveLoading}
                expanded={leaveExpanded}
                onToggle={() => setLeaveExpanded(o => !o)}
              />

              {/* Mobile only — Manager Notes */}
              {isMobile && (
                <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                  <button onClick={() => setShowNotes(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileText size={13} color={C.mid} />
                      <span style={{ fontSize: '11px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Manager Notes</span>
                      {notes.length > 0 && <span style={{ padding: '1px 7px', borderRadius: '20px', background: C.cream, color: C.mid, fontSize: '11px', fontWeight: 600, border: `1px solid ${C.border}` }}>{notes.length}</span>}
                    </div>
                    {showNotes ? <ChevronUp size={14} color={C.mid} /> : <ChevronDown size={14} color={C.mid} />}
                  </button>
                  {showNotes && (
                    <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {notes.length === 0 ? (
                        <div style={{ fontSize: '13px', color: C.mid, fontStyle: 'italic' }}>No notes yet</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {[...notes].reverse().map((n, i) => (
                            <div key={i} style={{ padding: '10px 12px', background: '#FEFCE8', borderRadius: '8px', border: '1px solid #F7EC71' }}>
                              <div style={{ fontSize: '11px', color: C.mid, marginBottom: '4px' }}>{n.author} · {fmtDT(n.created_at)}</div>
                              <div style={{ fontSize: '13px', color: C.charcoal, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{n.text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {isMgr && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                          <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note…" rows={2} style={{ ...inp, flex: 1, resize: 'vertical', fontFamily: 'inherit' }} />
                          <button onClick={addNote} disabled={saving || !noteText.trim()} style={{ padding: '8px 16px', background: saving || !noteText.trim() ? C.border : C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: saving || !noteText.trim() ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {saving ? '…' : 'Add'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Mobile only — Sensitive + Eitje settings (below notes) */}
              {isMobile && isAdmin && (
                <>
                  <div style={{ background: C.white, borderRadius: '12px', border: `1px solid ${C.red}33`, overflow: 'hidden' }}>
                    <button onClick={() => setShowSensitive(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: C.red, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sensitive</span>
                      {showSensitive ? <ChevronUp size={13} color={C.red} /> : <ChevronDown size={13} color={C.red} />}
                    </button>
                    {showSensitive && (
                      <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[['BSN', local.bsn || '—'], ['IBAN', local.iban || '—'], ['Nationality', local.nationality || '—']].map(([l, v]) => (
                          <div key={l}><div style={fieldStyle}>{l}</div><div style={valStyle}>{v}</div></div>
                        ))}
                        <div>
                          <div style={fieldStyle}>{local.employment_type === 'Full Time' ? 'Monthly salary' : 'Hourly rate'}</div>
                          <div style={valStyle}>
                            {local.employment_type === 'Full Time'
                              ? (local.gross_monthly_salary ? `€${parseFloat(local.gross_monthly_salary).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}` : '—')
                              : (local.hourly_rate ? `€${parseFloat(local.hourly_rate).toFixed(2)}/hr` : '—')}
                          </div>
                        </div>
                        {(local.id_doc_path || local.bsn_doc_path) && (
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                            {local.id_doc_path  && <MemberDocButton label='🪪 ID' path={local.id_doc_path} />}
                            {local.bsn_doc_path && <MemberDocButton label='📄 BSN' path={local.bsn_doc_path} />}
                          </div>
                        )}
                        <div style={{ fontSize: '10px', color: C.mid, fontStyle: 'italic' }}>Do not share or screenshot</div>
                      </div>
                    )}
                  </div>
                  <EitjeBalanceSettings
                    member={local}
                    currentUser={currentUser}
                    onSaved={payload => { const u = { ...local, ...payload }; setLocal(u); onUpdate(u); }}
                  />
                </>
              )}

            </div>
          </div>
        </div>
      </div>

      {showWarnings && (
        <WarningConductLog member={local} currentUser={currentUser} onClose={() => {
          setShowWarnings(false);
          supabase.from('warnings').select('id,internal_only').eq('team_member_id', local.id)
            .then(({ data }) => { if (data) { setWarningCount(data.length); setFormalWarningCount(data.filter(w => !w.internal_only).length); } });
        }} />
      )}
      {showChecklist  && <OnboardingChecklistModal member={local} currentUser={currentUser} onClose={() => setShowChecklist(false)} />}
      {showOnboarding && <SendOnboardingModal member={local} currentUser={currentUser} onClose={() => setShowOnboarding(false)} />}
      {showContract   && <ContractModal member={local} currentUser={currentUser} onClose={() => setShowContract(false)} onContractSaved={() => {
        supabase.from('contracts').select('id', { count: 'exact' }).eq('team_member_id', local.id).then(({ count }) => setContractCount(count || 0));
      }} />}
      {showEvaluation && (
        <EvaluationModal member={local} currentUser={currentUser} onClose={() => setShowEvaluation(false)} onSaved={() => {
          setShowEvaluation(false);
          supabase.from('evaluations').select('id,date').eq('team_member_id', local.id).order('date', { ascending: false })
            .then(({ data }) => { if (data) { setEvalCount(data.length); setLastEvalDate(data[0]?.date || null); } });
        }} />
      )}
      {showEvalHistory && (
        <EvaluationHistoryModal member={local} onClose={() => setShowEvalHistory(false)} onNew={() => { setShowEvalHistory(false); setShowEvaluation(true); }} />
      )}

      {/* Hours modal */}
      {showHoursModal && local.employment_type === 'Full Time' && (
        <LeaveOTMemberModal
          member={local}
          currentUser={currentUser}
          allEntries={leaveOtEntries}
          snapshots={leaveOtSnapshots}
          onClose={() => setShowHoursModal(false)}
          onMemberUpdated={u => { setLocal(u); onUpdate(u); }}
          onReloadMembers={() => {}}
        />
      )}

      {/* Pulse check — mid-contract eval */}
      {showPulseEval && (
        <EvaluationModal
          member={local}
          currentUser={currentUser}
          evalType="mid_contract"
          onClose={() => setShowPulseEval(false)}
          onSaved={() => {
            setShowPulseEval(false);
            const u = { ...local, pulse_dismissed: true, pulse_dismissed_at: new Date().toISOString() };
            setLocal(u); onUpdate(u);
            supabase.from('evaluations').select('id,date').eq('team_member_id', local.id).order('date', { ascending: false })
              .then(({ data }) => { if (data) { setEvalCount(data.length); setLastEvalDate(data[0]?.date || null); } });
          }}
        />
      )}

      {/* Renewal decision modal */}
      {showRenewal && (
        <RenewalModal
          member={local}
          currentUser={currentUser}
          onClose={() => setShowRenewal(false)}
          onSaved={(updates) => {
            const u = { ...local, ...updates };
            setLocal(u); onUpdate(u);
            setShowRenewal(false);
            // Refresh contract count if renewed
            supabase.from('contracts').select('id', { count: 'exact' }).eq('team_member_id', local.id).then(({ count }) => setContractCount(count || 0));
          }}
          onOpenContracts={() => { setShowRenewal(false); setShowContract(true); }}
        />
      )}

      {/* Onboarding popup modal */}
      {showOnboardingModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setShowOnboardingModal(false)}>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '440px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: C.navy, borderRadius: '16px 16px 0 0', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px', color: C.white }}>Onboarding</div>
                <div style={{ fontSize: '12px', color: C.skyBlue, marginTop: '2px' }}>{local.name} · {onbDone} of 4 steps complete</div>
              </div>
              <button onClick={() => setShowOnboardingModal(false)} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex' }}><X size={18} /></button>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Progress bar */}
              <div style={{ marginBottom: '4px' }}>
                <div style={{ height: '6px', borderRadius: '99px', background: C.border, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.round((onbDone / 4) * 100)}%`, background: onbComplete ? C.green : C.amber, borderRadius: '99px', transition: 'width 0.3s' }}/>
                </div>
              </div>
              {/* TM steps */}
              <div style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Employee steps</div>
              {[
                { field: 'onboarding_pack_confirmed', label: '📩 Onboarding pack confirmed', val: packConfirmed,
                  onClick: () => { const v = !local.onboarding_pack_confirmed; supabase.from('team_members').update({ onboarding_pack_confirmed: v }).eq('id', local.id).then(({ error }) => { if (!error) { const u = { ...local, onboarding_pack_confirmed: v }; setLocal(u); onUpdate(u); setPackConfirmed(v); }}); }},
                { field: 'onboarding_checklist_done', label: '📋 Checklist completed',       val: local.onboarding_checklist_done, onClick: () => toggleCheck('onboarding_checklist_done') },
                { field: 'eval_done',                 label: '⭐ First evaluation completed', val: evalDone, readOnly: true },
                { field: 'probation_done',            label: '✅ Onboarding sign-off',        val: local.probation_done,
                  onClick: () => { if (packConfirmed && local.onboarding_checklist_done && evalDone) toggleCheck('probation_done'); },
                  locked: !(packConfirmed && local.onboarding_checklist_done && evalDone) },
              ].map(({ field, label, val, onClick, locked, readOnly }) => (
                <div key={field} onClick={locked || readOnly ? undefined : onClick}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: val ? C.greenLight : locked ? C.cream : readOnly ? C.skyBlueLight : C.white, border: `1px solid ${val ? C.green + '44' : readOnly ? C.skyBlue + '44' : C.border}`, borderRadius: '10px', cursor: locked || readOnly ? 'default' : 'pointer', opacity: locked ? 0.6 : 1, transition: 'background 0.15s' }}>
                  {val ? <CheckCircle size={16} color={C.green} /> : <Circle size={16} color={locked ? C.border : readOnly ? C.skyText : C.mid} />}
                  <span style={{ fontSize: '13px', color: val ? C.green : readOnly ? C.skyText : C.charcoal, fontWeight: val ? 600 : 400, flex: 1 }}>{label}</span>
                  {locked   && <span style={{ fontSize: '10px', color: C.mid }}>locked</span>}
                  {readOnly && !val && <span style={{ fontSize: '10px', color: C.skyText, fontStyle: 'italic' }}>auto · complete first eval</span>}
                  {readOnly &&  val && <span style={{ fontSize: '10px', color: C.green, fontWeight: 700 }}>✓ auto</span>}
                </div>
              ))}
              {/* Admin steps */}
              {isAdmin && (
                <>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: C.mid, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '8px', marginBottom: '2px' }}>Admin steps</div>
                  {[
                    ['nmbrs_profile_done',  'NMBRS profile created'],
                    ['contract_signed',      'Contract signed'],
                    ['uniform_deposit_paid', `Uniform deposit collected (€${depositAmount(local.dept)})`],
                  ].map(([f, l]) => {
                    const done = !!local[f];
                    return (
                      <div key={f} onClick={() => toggleCheck(f)}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: done ? C.greenLight : C.white, border: `1px solid ${done ? C.green + '44' : C.border}`, borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s' }}>
                        {done ? <CheckCircle size={16} color={C.green} /> : <Circle size={16} color={C.mid} />}
                        <span style={{ fontSize: '13px', color: done ? C.green : C.charcoal, fontWeight: done ? 600 : 400, flex: 1 }}>{l}</span>
                      </div>
                    );
                  })}
                </>
              )}
              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', paddingTop: '12px', borderTop: `1px solid ${C.border}` }}>
                <button onClick={() => { setShowOnboardingModal(false); setShowChecklist(true); }}
                  style={{ flex: 1, padding: '9px', background: C.navy, color: C.white, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  📋 Open checklist
                </button>
                <button onClick={() => { setShowOnboardingModal(false); setShowOnboarding(true); }}
                  style={{ flex: 1, padding: '9px', background: C.white, color: C.navy, border: `1px solid ${C.navy}44`, borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                  📩 Send pack
                </button>
              </div>
              {/* Admin override — mark all complete for legacy TMs */}
              {isAdmin && !onbComplete && (
                <LegacyCompleteButton
                  member={local}
                  onDone={(u) => { setLocal(u); onUpdate(u); setPackConfirmed(true); setShowOnboardingModal(false); }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Training popup modal */}
      {showTrainingModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setShowTrainingModal(false)}>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '420px', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background: C.navy, borderRadius: '16px 16px 0 0', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '15px', color: C.white }}>Training</div>
                <div style={{ fontSize: '12px', color: C.skyBlue, marginTop: '2px' }}>{local.name} · {tDone} of {mTrainings.length} complete</div>
              </div>
              <button onClick={() => setShowTrainingModal(false)} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '8px', cursor: 'pointer', color: C.white, padding: '6px', display: 'flex' }}><X size={18} /></button>
            </div>
            {/* Body */}
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Progress bar */}
              <div style={{ marginBottom: '4px' }}>
                <div style={{ height: '6px', borderRadius: '99px', background: C.border, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${mTrainings.length ? Math.round((tDone / mTrainings.length) * 100) : 0}%`, background: tDone === mTrainings.length ? C.green : C.skyText, borderRadius: '99px', transition: 'width 0.3s' }}/>
                </div>
              </div>
              {mTrainings.length === 0 ? (
                <div style={{ fontSize: '13px', color: C.mid, padding: '12px 0' }}>No trainings assigned to this dept.</div>
              ) : (
                mTrainings.map(t => (
                  <div key={t.id}
                    onClick={isMgr ? () => toggleTraining(t.id) : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: t.done ? C.greenLight : C.white, borderRadius: '10px', border: `1px solid ${t.done ? C.green + '44' : C.border}`, cursor: isMgr ? 'pointer' : 'default', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (isMgr) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {t.done ? <CheckCircle size={16} color={C.green} /> : <Circle size={16} color={C.mid} />}
                    <span style={{ fontSize: '13px', color: t.done ? C.green : C.charcoal, fontWeight: t.done ? 600 : 400, flex: 1 }}>{t.title}</span>
                    {t.required && !t.done && <span style={{ fontSize: '10px', fontWeight: 700, color: C.red, background: C.redLight, padding: '2px 7px', borderRadius: '10px', flexShrink: 0 }}>REQUIRED</span>}
                    {t.required && t.done  && <span style={{ fontSize: '10px', fontWeight: 700, color: C.green, background: C.greenLight, padding: '2px 7px', borderRadius: '10px', flexShrink: 0 }}>✓ REQ</span>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}